import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useToast } from '../context/ToastContext';
import {
  useGetSettingsQuery,
  useDeleteSettingMutation,
  useCreateSettingMutation,
  useUpdateSettingMutation,
} from '../redux/services/settingsApi';
import {SETTING_TYPES, SETTING_TYPE_LABELS, DATE_COMPARISON_LABELS, DATE_COMPARISON_COM_LABELS, 
  TRI_STATE, TRI_STATE_LABELS} from '../contants';

// Local storage keys
const STORAGE_KEY = 'settingsFilters';

const getSavedFilters = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {
      pageSize: 25,
      search: '',
      settingsType: 'all'
    };
  } catch (e) {
    return {
      pageSize: 25,
      search: '',
      settingsType: 'all'
    };
  }
};

const Settings = () => {
  const { showToast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  
  // Get saved filters on component mount
  const initialFilters = getSavedFilters();
  
  // Pagination state with localStorage initialization
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => {
    return initialFilters.pageSize || 25;
  });
  
  // Loading states
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  
  // Pagination model for DataGrid
  const [paginationModel, setPaginationModel] = useState({
    pageSize: pageSize,
    page: page,
  });
  
  // Add new state for settings type filter
  const [settingsTypeFilter, setSettingsTypeFilter] = useState('all');
  
  // Save filters to localStorage when they change
  useEffect(() => {
    const filters = {
      pageSize: pageSize,
      search: searchQuery,
      settingsType: settingsTypeFilter
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [pageSize, searchQuery, settingsTypeFilter]);
  
  // Load initial search query from saved filters
  useEffect(() => {
    if (initialFilters.search) {
      setSearchQuery(initialFilters.search);
      setActiveSearchQuery(initialFilters.search);
    }
    // eslint-disable-next-line
  }, []);

  // Load initial settingsType from saved filters
  useEffect(() => {
    if (initialFilters.settingsType) {
      setSettingsTypeFilter(initialFilters.settingsType);
    }
    // eslint-disable-next-line
  }, []);

  const getSettingsTypeFromFilter = (filter) => {
    switch (filter) {
      case 'default': return SETTING_TYPES.DEFAULT;
      case 'extended': return SETTING_TYPES.EXTENDED;
      case 'marafon_1': return SETTING_TYPES.MARAFON_1;
      case 'marafon_2': return SETTING_TYPES.MARAFON_2;
      default: return null;
    }
  };

  const isExtendedType = (settingsType) => {
    const type = parseInt(settingsType);
    return type === SETTING_TYPES.EXTENDED || 
           type === SETTING_TYPES.MARAFON_1 || 
           type === SETTING_TYPES.MARAFON_2;
  };

  const {
    data: settings,
    isLoading: isSettingsLoading,
    error: settingsError,
    refetch,
  } = useGetSettingsQuery({
    ...(activeSearchQuery ? { search: activeSearchQuery } : {}),
    ...(settingsTypeFilter !== 'all' ? { 
      settings_type: getSettingsTypeFromFilter(settingsTypeFilter)
    } : {})
  });

  const [deleteSetting] = useDeleteSettingMutation();
  const [createSetting, { isLoading: isCreating }] = useCreateSettingMutation();
  const [updateSetting, { isLoading: isUpdating }] = useUpdateSettingMutation();

  // Safely extract the array of settings from the API response
  const settingsArray = useMemo(() => {
    if (!settings) return [];
    
    // Check if settings is an array or has a data property
    let dataArray = [];
    if (Array.isArray(settings)) {
      dataArray = settings;
    } else if (settings.items && Array.isArray(settings.items)) {
      dataArray = settings.items;
    }
    
    // Filter out undefined items and ensure each item has an id
    return dataArray.filter(item => item && typeof item === 'object').map(item => {
      if (item.id === undefined || item.id === null) {
        return { ...item, id: `temp-id-${Math.random().toString(36).substr(2, 9)}` };
      }
      return item;
    });
  }, [settings]);

  const [settingForm, setSettingForm] = useState({
    settings_type: SETTING_TYPES.DEFAULT,
    title: '',
    uniq_name: '',
    value: '',
    last_marafon: TRI_STATE.NA,
    time_from: '',
    time_to: '',
    web1: 0,
    web2: 0,
    is_sale: 0,
    is_order: 0,
    is_prepay: 0,
    text1: TRI_STATE.NA,
    text2: TRI_STATE.NA,
    text3: TRI_STATE.NA,
    text5: TRI_STATE.NA,
    text6: TRI_STATE.NA,
    text7: TRI_STATE.NA,
    text8: TRI_STATE.NA,
    text9: TRI_STATE.NA,
    cur_date: 0,
    plus_days: '',
  });

  const handleDeleteClick = (setting) => {
    setSelectedSetting(setting);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteSetting(selectedSetting.id).unwrap();
      showToast('Setting deleted successfully', 'success');
      refetch();
    } catch (err) {
      showToast(
        err.data?.message || 'Failed to delete setting', 
        'error'
      );
    } finally {
      setDeleteDialogOpen(false);
      setSelectedSetting(null);
    }
  };

  // Handle pagination model change (page size and current page)
  const handlePaginationModelChange = async (model) => {
    setIsPaginationLoading(true);
    setPaginationModel(model);
    setPage(model.page);
    setPageSize(model.pageSize);
    
    // Save to localStorage
    const filters = {
      ...getSavedFilters(),
      pageSize: model.pageSize
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    
    try {
      // Only refetch if your API supports server-side pagination
      // If you're doing client-side pagination, you can skip this
      // await refetch();
      await new Promise(resolve => setTimeout(resolve, 100));
    } finally {
      setIsPaginationLoading(false);
    }
  };

  // Handle search submit
  const handleSearchSubmit = async () => {
    setIsSearchLoading(true);
    setActiveSearchQuery(searchQuery);
    // Reset to first page when searching
    setPage(0);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    
    try {
      await refetch({
        ...(searchQuery ? { search: searchQuery } : {}),
        ...(settingsTypeFilter !== 'all' ? { 
          settings_type: getSettingsTypeFromFilter(settingsTypeFilter)
        } : {})
      });
      await new Promise(resolve => setTimeout(resolve, 100));
    } finally {
      setIsSearchLoading(false);
    }
  };
  
  // Handle pressing Enter in search field
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  // Handle clearing filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setActiveSearchQuery('');
    setSettingsTypeFilter('all');
    setPage(0);
    setPaginationModel({
      pageSize: 25,
      page: 0
    });
    localStorage.removeItem(STORAGE_KEY);
  };

  // Update the settings type filter change handler to trigger a refetch
  const handleSettingsTypeChange = async (e) => {
    const newValue = e.target.value;
    setSettingsTypeFilter(newValue);
    setIsSearchLoading(true);
    
    try {
      await refetch({
        ...(activeSearchQuery ? { search: activeSearchQuery } : {}),
        ...(newValue !== 'all' ? { 
          settings_type: getSettingsTypeFromFilter(newValue)
        } : {})
      });
    } finally {
      setIsSearchLoading(false);
    }
  };

  const isLoading = isSettingsLoading || isCreating || isUpdating || isPaginationLoading || isSearchLoading;

  // Only show loading state if we're fetching the initial data
  if (isSettingsLoading && !settings) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <CircularProgress size={40} />
        <Typography sx={{ mt: 2 }}>Loading settings...</Typography>
      </Box>
    );
  }

  // Add additional debugging for the error state
  if (settingsError) {
    return (
      <Typography variant="h4" color="error" sx={{ textAlign: 'center', mt: 4 }}>
        Error loading settings: {settingsError.status} {settingsError.data?.message || 'Unknown error'}
      </Typography>
    );
  }

  // Define DataGrid columns with better error handling
  const columns = [
    { 
      field: 'settings_type', 
      headerName: 'Type', 
      width: 80,
      valueGetter: (params) => {
        if (!params) return '';
        return SETTING_TYPE_LABELS[params] || `Type ${params}` || 'Unknown';
      },
    },
    { 
      field: 'title', 
      headerName: 'Title', 
      width: 150,
      flex: 1,
      valueGetter: (params) => {
        if (!params) return '';
        return params || '';
      },
    },
    { 
      field: 'uniq_name', 
      headerName: 'Unique Name', 
      width: 120,
      flex: 1,
      valueGetter: (params) => {
        if (!params) return '';
        return params || '';
      },
    },
    { 
      field: 'last_marafon', 
      headerName: 'Last Marathon', 
      width: 100,
      renderCell: (params) => {
        if (!params || !params.row || !isExtendedType(params.row.settings_type)) return '-';
        return TRI_STATE_LABELS[params.row.last_marafon ?? 0];
      },
    },
    { 
      field: 'cur_date', 
      headerName: 'Cur. Date', 
      width: 110,
      renderCell: (params) => {
        if (!params || !params.row || !isExtendedType(params.row.settings_type)) return '-';
        
        const dateCompValue = params.row.cur_date || params.row.date_comparison;
        if (!dateCompValue || dateCompValue === 0) return '-';
        
        const label = DATE_COMPARISON_COM_LABELS[dateCompValue] || `Value ${dateCompValue}`;
        const plusDays = params.row.plus_days && params.row.plus_days !== '0' ? params.row.plus_days : '';
        
        return `${label}${plusDays !== '' ? `+${plusDays}` : ''}`;
      },
    },
    { 
      field: 'time_window', 
      headerName: 'Time Window', 
      width: 150,
      renderCell: (params) => {
        if (!params || !params.row || !isExtendedType(params.row.settings_type) || (!params.row.time_from && !params.row.time_to)) return '-';
        return `${params.row.time_from || '-'} to ${params.row.time_to || '-'}`;
      },
    },
    { 
      field: 'web', 
      headerName: 'Web Settings', 
      width: 130,
      renderCell: (params) => {
        if (!params || !params.row || !isExtendedType(params.row.settings_type)) return '-';
        
        const webs = [];
        
        // Add Web1 flag
        if (params.row.web1 === TRI_STATE.YES) {
          webs.push(<span key="web1" style={{color: 'green', marginRight: '4px'}}>Web1</span>);
        } else if (params.row.web1 === TRI_STATE.NO) {
          webs.push(<span key="web1" style={{color: 'red', marginRight: '4px'}}>Web1</span>);
        }
        
        // Add Web2 flag
        if (params.row.web2 === TRI_STATE.YES) {
          webs.push(<span key="web2" style={{color: 'green', marginRight: '4px'}}>Web2</span>);
        } else if (params.row.web2 === TRI_STATE.NO) {
          webs.push(<span key="web2" style={{color: 'red', marginRight: '4px'}}>Web2</span>);
        }
        
        return webs.length > 0 ? webs : '-';
      },
    },
    { 
      field: 'flags', 
      headerName: 'Flags', 
      width: 150,
      renderCell: (params) => {
        if (!params || !params.row || !isExtendedType(params.row.settings_type)) return '-';
        
        const flags = [];
         
        // Add Order flag
        if (params.row.is_order === TRI_STATE.YES) {
          flags.push(<span key="order" style={{color: 'green', marginRight: '4px'}}>Order</span>);
        } else if (params.row.is_order === TRI_STATE.NO) {
          flags.push(<span key="order" style={{color: 'red', marginRight: '4px'}}>Order</span>);
        }
        
        // Add Sale flag
        if (params.row.is_sale === TRI_STATE.YES) {
          flags.push(<span key="sale" style={{color: 'green', marginRight: '4px'}}>Sale</span>);
        } else if (params.row.is_sale === TRI_STATE.NO) {
          flags.push(<span key="sale" style={{color: 'red', marginRight: '4px'}}>Sale</span>);
        }
        
        // Add Prepay flag
        if (params.row.is_prepay === TRI_STATE.YES) {
          flags.push(<span key="prepay" style={{color: 'green', marginRight: '4px'}}>Prepay</span>);
        } else if (params.row.is_prepay === TRI_STATE.NO) {
          flags.push(<span key="prepay" style={{color: 'red', marginRight: '4px'}}>Prepay</span>);
        }
        
        return flags.length > 0 ? flags : '-';
      },
    },
    { 
      field: 'text_fields', 
      headerName: 'Text Fields', 
      width: 200,
      renderCell: (params) => {
        if (!params || !params.row || !isExtendedType(params.row.settings_type)) return '-';
        
        const textFields = [];
        
        // Check all text fields (text1, text2, text3, text5-text9)
        const textFieldNames = ['text1', 'text2', 'text3', 'text5', 'text6', 'text7', 'text8', 'text9'];
        
        textFieldNames.forEach(fieldName => {
          const fieldValue = params.row[fieldName];
          if (fieldValue === TRI_STATE.YES) {
            textFields.push(<span key={fieldName} style={{color: 'green', marginRight: '4px'}}>{fieldName.toUpperCase()}</span>);
          } else if (fieldValue === TRI_STATE.NO) {
            textFields.push(<span key={fieldName} style={{color: 'red', marginRight: '4px'}}>{fieldName.toUpperCase()}</span>);
          }
        });
        
        return textFields.length > 0 ? textFields : '-';
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        
        return (
          <Box>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleEditClick(params.row)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDeleteClick(params.row)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for plus_days
    if (name === 'plus_days') {
      // Remove any spaces and non-numeric/non-comma characters
      const cleanValue = value.replace(/[^\d,]/g, '');
      setSettingForm(prev => ({
        ...prev,
        [name]: cleanValue
      }));
      return;
    }
    
    // Format time input fields
    if ((name === 'time_from' || name === 'time_to') && value) {
      // Allow only numbers and colon
      let formattedValue = value.replace(/[^\d:]/g, '');
      
      // Format as HH:MM
      if (formattedValue.length > 0) {
        // Remove any existing colons
        formattedValue = formattedValue.replace(/:/g, '');
        
        if (formattedValue.length > 2) {
          // Insert colon after the first two digits (hours)
          formattedValue = `${formattedValue.substring(0, 2)}:${formattedValue.substring(2, 4)}`;
        }
        
        // Validate hours (0-23) and minutes (0-59)
        const parts = formattedValue.split(':');
        if (parts[0] && parseInt(parts[0]) > 23) {
          parts[0] = '23';
        }
        if (parts[1] && parseInt(parts[1]) > 59) {
          parts[1] = '59';
        }
        
        formattedValue = parts.join(':');
      }
      
      setSettingForm(prev => ({
        ...prev,
        [name]: formattedValue
      }));
      return;
    }
    
    setSettingForm(prev => ({
      ...prev,
      [name]: value,
      // Clear related fields if last_marafon is set to NO or N/A
      ...(name === 'last_marafon' && (value === TRI_STATE.NO.toString() || value === TRI_STATE.NA.toString()) ? {
        cur_date: 0,
        plus_days: '',
        time_from: '',
        time_to: ''
      } : {})
    }));
  };

  const handleEditClick = (setting) => {
    setSelectedSetting(setting);
    setSettingForm({
      settings_type: setting.settings_type || SETTING_TYPES.DEFAULT,
      title: setting.title || '',
      uniq_name: setting.uniq_name || '',
      value: setting.value || '',
      last_marafon: setting.last_marafon ?? TRI_STATE.NA,
      time_from: setting.time_from || '',
      time_to: setting.time_to || '',
      web1: setting.web1 ?? TRI_STATE.NA,
      web2: setting.web2 ?? TRI_STATE.NA,
      is_sale: setting.is_sale ?? TRI_STATE.NA,
      is_order: setting.is_order ?? TRI_STATE.NA,
      is_prepay: setting.is_prepay ?? TRI_STATE.NA,
      text1: setting.text1 ?? TRI_STATE.NA,
      text2: setting.text2 ?? TRI_STATE.NA,
      text3: setting.text3 ?? TRI_STATE.NA,
      text5: setting.text5 ?? TRI_STATE.NA,
      text6: setting.text6 ?? TRI_STATE.NA,
      text7: setting.text7 ?? TRI_STATE.NA,
      text8: setting.text8 ?? TRI_STATE.NA,
      text9: setting.text9 ?? TRI_STATE.NA,
      cur_date: setting.cur_date || setting.date_comparison || 0,
      plus_days: setting.plus_days || ''
    });
    setEditDialogOpen(true);
  };

  const handleCreateClick = () => {
    // Set settings_type based on current filter, default to DEFAULT if filter is 'all'
    const defaultSettingsType = settingsTypeFilter === 'all' 
      ? SETTING_TYPES.DEFAULT 
      : getSettingsTypeFromFilter(settingsTypeFilter) || SETTING_TYPES.DEFAULT;

    setSettingForm({
      settings_type: defaultSettingsType,
      title: '',
      value: '',
      last_marafon: TRI_STATE.NA,
      time_from: '',
      time_to: '',
      web1: TRI_STATE.NA,
      web2: TRI_STATE.NA,
      is_sale: TRI_STATE.NA,
      is_order: TRI_STATE.NA,
      is_prepay: TRI_STATE.NA,
      text1: TRI_STATE.NA,
      text2: TRI_STATE.NA,
      text3: TRI_STATE.NA,
      text5: TRI_STATE.NA,
      text6: TRI_STATE.NA,
      text7: TRI_STATE.NA,
      text8: TRI_STATE.NA,
      text9: TRI_STATE.NA,
      cur_date: TRI_STATE.NA,
      plus_days: ''
    });
    setCreateDialogOpen(true);
  };

  const handlePlusDaysKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const currentValue = String(settingForm.plus_days || '');
      const lastNumber = currentValue.split(',').pop();
      
      if (lastNumber) {
        setSettingForm(prev => ({
          ...prev,
          plus_days: currentValue + (currentValue.endsWith(',') ? '' : ',')
        }));
      }
    }
  };

  const handleCreateSetting = async () => {
    try {
      if (!settingForm.uniq_name) {
        showToast('Unique name is required', 'error');
        return;
      }
      
      const settingData = {
        ...settingForm,
        settings_type: parseInt(settingForm.settings_type, 10),
        cur_date: parseInt(settingForm.cur_date, 10),
        plus_days: settingForm.plus_days.replace(/,+$/, ''),
        last_marafon: isNaN(parseInt(settingForm.last_marafon, 10)) ? TRI_STATE.NA : parseInt(settingForm.last_marafon, 10),
        web1: isNaN(parseInt(settingForm.web1, 10)) ? TRI_STATE.NA : parseInt(settingForm.web1, 10),
        web2: isNaN(parseInt(settingForm.web2, 10)) ? TRI_STATE.NA : parseInt(settingForm.web2, 10),
        is_sale: isNaN(parseInt(settingForm.is_sale, 10)) ? TRI_STATE.NA : parseInt(settingForm.is_sale, 10),
        is_order: isNaN(parseInt(settingForm.is_order, 10)) ? TRI_STATE.NA : parseInt(settingForm.is_order, 10),
        is_prepay: isNaN(parseInt(settingForm.is_prepay, 10)) ? TRI_STATE.NA : parseInt(settingForm.is_prepay, 10),
        text1: isNaN(parseInt(settingForm.text1, 10)) ? TRI_STATE.NA : parseInt(settingForm.text1, 10),
        text2: isNaN(parseInt(settingForm.text2, 10)) ? TRI_STATE.NA : parseInt(settingForm.text2, 10),
        text3: isNaN(parseInt(settingForm.text3, 10)) ? TRI_STATE.NA : parseInt(settingForm.text3, 10),
        text5: isNaN(parseInt(settingForm.text5, 10)) ? TRI_STATE.NA : parseInt(settingForm.text5, 10),
        text6: isNaN(parseInt(settingForm.text6, 10)) ? TRI_STATE.NA : parseInt(settingForm.text6, 10),
        text7: isNaN(parseInt(settingForm.text7, 10)) ? TRI_STATE.NA : parseInt(settingForm.text7, 10),
        text8: isNaN(parseInt(settingForm.text8, 10)) ? TRI_STATE.NA : parseInt(settingForm.text8, 10),
        text9: isNaN(parseInt(settingForm.text9, 10)) ? TRI_STATE.NA : parseInt(settingForm.text9, 10)
      };
      
      await createSetting(settingData).unwrap();
      showToast('Setting created successfully', 'success');
      setCreateDialogOpen(false);
      refetch();
    } catch (error) {
      showToast(error.data?.message || 'Failed to create setting', 'error');
    }
  };

  const handleUpdateSetting = async () => {
    try {
      if (!settingForm.uniq_name) {
        showToast('Unique name is required', 'error');
        return;
      }
      
      if (!selectedSetting || !selectedSetting.id) {
        showToast('Cannot update setting: Missing ID', 'error');
        return;
      }
      
      const settingData = {
        settingId: selectedSetting.id,
        ...settingForm,
        settings_type: parseInt(settingForm.settings_type, 10),
        cur_date: parseInt(settingForm.cur_date, 10),
        plus_days: String(settingForm.plus_days || '').replace(/,+$/, ''),
        last_marafon: isNaN(parseInt(settingForm.last_marafon, 10)) ? TRI_STATE.NA : parseInt(settingForm.last_marafon, 10),
        web1: isNaN(parseInt(settingForm.web1, 10)) ? TRI_STATE.NA : parseInt(settingForm.web1, 10),
        web2: isNaN(parseInt(settingForm.web2, 10)) ? TRI_STATE.NA : parseInt(settingForm.web2, 10),
        is_sale: isNaN(parseInt(settingForm.is_sale, 10)) ? TRI_STATE.NA : parseInt(settingForm.is_sale, 10),
        is_order: isNaN(parseInt(settingForm.is_order, 10)) ? TRI_STATE.NA : parseInt(settingForm.is_order, 10),
        is_prepay: isNaN(parseInt(settingForm.is_prepay, 10)) ? TRI_STATE.NA : parseInt(settingForm.is_prepay, 10),
        text1: isNaN(parseInt(settingForm.text1, 10)) ? TRI_STATE.NA : parseInt(settingForm.text1, 10),
        text2: isNaN(parseInt(settingForm.text2, 10)) ? TRI_STATE.NA : parseInt(settingForm.text2, 10),
        text3: isNaN(parseInt(settingForm.text3, 10)) ? TRI_STATE.NA : parseInt(settingForm.text3, 10),
        text5: isNaN(parseInt(settingForm.text5, 10)) ? TRI_STATE.NA : parseInt(settingForm.text5, 10),
        text6: isNaN(parseInt(settingForm.text6, 10)) ? TRI_STATE.NA : parseInt(settingForm.text6, 10),
        text7: isNaN(parseInt(settingForm.text7, 10)) ? TRI_STATE.NA : parseInt(settingForm.text7, 10),
        text8: isNaN(parseInt(settingForm.text8, 10)) ? TRI_STATE.NA : parseInt(settingForm.text8, 10),
        text9: isNaN(parseInt(settingForm.text9, 10)) ? TRI_STATE.NA : parseInt(settingForm.text9, 10)
      };
      
      await updateSetting(settingData).unwrap();
      showToast('Setting updated successfully', 'success');
      setEditDialogOpen(false);
      refetch();
    } catch (error) {
      console.error('Error updating setting:', error);
      showToast(error.data?.message || 'Failed to update setting', 'error');
    }
  };

  return (
    <Paper elevation={1} sx={{ width: '100%', borderRadius: 1, p: 2, mb: 2 }}>
      <Box sx={{ width: '100%', p: 2, mb: 2 }}>      
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <SettingsIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            System Settings
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, maxWidth: 700 }}>
            <TextField
              select
              size="small"
              value={settingsTypeFilter}
              onChange={handleSettingsTypeChange}
              sx={{ minWidth: 120, mr: 1 }}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Types</option>
              <option value="default">Default</option>
              <option value="extended">Extended</option>
              <option value="marafon_1">Marathon 1</option>
              <option value="marafon_2">Marathon 2</option>
            </TextField>

            <TextField
              sx={{ flex: 1 }}
              variant="outlined"
              size="small"
              placeholder="Search settings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              sx={{ ml: 1 }}
              variant="contained"
              color="primary"
              onClick={handleSearchSubmit}
              disabled={isLoading}
            >
              {isSearchLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Search'
              )}
            </Button>

            {searchQuery && (
              <Button
                sx={{ ml: 1 }}
                variant="outlined"
                onClick={handleClearFilters}
              >
                Clear
              </Button>
            )}
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
          >
            Add Setting
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* DataGrid with correct ID handling */}
        <Box sx={{ height: 'calc(97vh - 250px)', width: '100%', mt: 2 }}>
          {settingsArray.length === 0 && !isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
              <Typography color="text.secondary">No settings found</Typography>
            </Box>
          ) : (
            <DataGrid
              rows={settingsArray}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              pageSizeOptions={[25, 50, 100]}
              disableRowSelectionOnClick
              disableColumnMenu
              loading={isLoading}
              getRowId={(row) => {
                if (row.id === undefined || row.id === null) {
                  console.warn('Row missing ID in getRowId:', row);
                  return `temp-${Math.random()}`;
                }
                return row.id;
              }}
              componentsProps={{
                noRowsOverlay: {
                  style: {
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }
                }
              }}
            />
          )}
        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>
            Confirm Delete
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this setting? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setDeleteDialogOpen(false)} 
              color="primary"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteConfirm} 
              color="error" 
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Setting Form Dialog (shared between edit and create) */}
        <Dialog 
          open={editDialogOpen || createDialogOpen} 
          onClose={() => editDialogOpen ? setEditDialogOpen(false) : setCreateDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editDialogOpen ? 'Edit Setting' : 'Create New Setting'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
              <TextField
                margin="dense"
                label="Settings Type"
                name="settings_type"
                fullWidth
                select
                value={settingForm.settings_type}
                onChange={handleInputChange}
                variant="outlined"
                SelectProps={{
                  native: true,
                }}
              >
                <option value={SETTING_TYPES.DEFAULT}>
                  {SETTING_TYPE_LABELS[SETTING_TYPES.DEFAULT]}
                </option>
                <option value={SETTING_TYPES.EXTENDED}>
                  {SETTING_TYPE_LABELS[SETTING_TYPES.EXTENDED]}
                </option>
                <option value={SETTING_TYPES.MARAFON_1}>
                  {SETTING_TYPE_LABELS[SETTING_TYPES.MARAFON_1]}
                </option>
                <option value={SETTING_TYPES.MARAFON_2}>
                  {SETTING_TYPE_LABELS[SETTING_TYPES.MARAFON_2]}
                </option>
              </TextField>
              
              <TextField
                margin="dense"
                label="Title"
                name="title"
                fullWidth
                value={settingForm.title}
                onChange={handleInputChange}
                variant="outlined"
              />
              
              <TextField
                required
                margin="dense"
                label="Unique Name"
                name="uniq_name"
                fullWidth
                value={settingForm.uniq_name}
                onChange={handleInputChange}
                variant="outlined"
                error={!settingForm.uniq_name}
                helperText={!settingForm.uniq_name ? "Unique name is required" : ""}
              />
              
              <TextField
                margin="dense"
                label="Value"
                name="value"
                fullWidth
                value={settingForm.value}
                onChange={handleInputChange}
                variant="outlined"
                multiline
                rows={4}
              />
              
              {/* Only show additional fields if settings_type is EXTENDED, MARAFON_1, or MARAFON_2 */}
              {isExtendedType(settingForm.settings_type) && (
                <>
                  {/* Last Marathon select */}
                  <TextField
                    select
                    margin="dense"
                    label="Last Marathon"
                    name="last_marafon"
                    value={settingForm.last_marafon}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 120 }}
                    SelectProps={{
                      native: true
                    }}
                  >
                    {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </TextField>
                  
                  {/* Date comparison selector and plus_days input */}
                    <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 1, alignItems: 'center' }}>
                      <TextField
                        margin="dense"
                        label="Current Date"
                        name="cur_date"
                        select
                        fullWidth
                        value={settingForm.cur_date}
                        onChange={handleInputChange}
                        variant="outlined"
                        SelectProps={{
                          native: true,
                        }}
                        sx={{ mt: 0 }}
                      >
                        {Object.entries(DATE_COMPARISON_LABELS)
                          .filter(([value]) => +settingForm.last_marafon === TRI_STATE.YES || (+settingForm.last_marafon !== TRI_STATE.YES && (parseInt(value) === 0 || (parseInt(value) > 3 && parseInt(value) < 7))))
                          .map(([value, label]) => (
                            <option key={value} value={parseInt(value)}>
                              {label}
                            </option>
                          ))}
                      </TextField>
                      
                      {settingForm.cur_date !== 0 && (
                        <TextField
                          margin="dense"
                          label="Plus Days"
                          name="plus_days"
                          fullWidth
                          value={settingForm.plus_days}
                          onChange={handleInputChange}
                          onKeyDown={handlePlusDaysKeyDown}
                          variant="outlined"
                          placeholder="Enter numbers, press Enter to add more"
                          helperText="Press Enter after each number to add multiple values"
                          sx={{ mt: 3 }}
                        />
                      )}
                    </Box>
                
                  <small>The time check works for the date that is equal to the value in the Current Date field.</small>
                  
                  <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 1 }}>
                    <TextField
                      margin="dense"
                      label="Time From"
                      name="time_from"
                      fullWidth
                      value={settingForm.time_from}
                      onChange={handleInputChange}
                      variant="outlined"
                      placeholder="HH:MM"
                      inputProps={{
                        maxLength: 5,
                        pattern: "[0-9]{2}:[0-9]{2}"
                      }}
                      helperText="Format: HH:MM (24-hour)"
                      sx={{ mt: 0 }}
                    />
                    <TextField
                      margin="dense"
                      label="Time To"
                      name="time_to"
                      fullWidth
                      value={settingForm.time_to}
                      onChange={handleInputChange}
                      variant="outlined"
                      placeholder="HH:MM"
                      inputProps={{
                        maxLength: 5,
                        pattern: "[0-9]{2}:[0-9]{2}"
                      }}
                      helperText="Format: HH:MM (24-hour)"
                      sx={{ mt: 0 }}
                    />
                  </Box>
                
                  {/* All selects in one row */}
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 1 }}>
                    <TextField
                      select
                      label="Web1"
                      name="web1"
                      value={settingForm.web1}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Web2"
                      name="web2"
                      value={settingForm.web2}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Order"
                      name="is_order"
                      value={settingForm.is_order}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Sale"
                      name="is_sale"
                      value={settingForm.is_sale}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Prepay"
                      name="is_prepay"
                      value={settingForm.is_prepay}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                  </Box>
                  
                  {/* First row of text fields: text1, text2, text3, text5, text6 */}
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 1 }}>
                    <TextField
                      select
                      label="Text1"
                      name="text1"
                      value={settingForm.text1}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Text2"
                      name="text2"
                      value={settingForm.text2}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Text3"
                      name="text3"
                      value={settingForm.text3}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Text5"
                      name="text5"
                      value={settingForm.text5}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Text6"
                      name="text6"
                      value={settingForm.text6}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                  </Box>
                  
                  {/* Second row of text fields: text7, text8, text9 */}
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 1 }}>
                    <TextField
                      select
                      label="Text7"
                      name="text7"
                      value={settingForm.text7}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Text8"
                      name="text8"
                      value={settingForm.text8}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Text9"
                      name="text9"
                      value={settingForm.text9}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                  </Box>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => editDialogOpen ? setEditDialogOpen(false) : setCreateDialogOpen(false)} 
              color="primary"
            >
              Cancel
            </Button>
            <Button 
              onClick={editDialogOpen ? handleUpdateSetting : handleCreateSetting} 
              color="primary" 
              variant="contained"
            >
              {editDialogOpen ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Paper>
  );
};

export default Settings; 